import { MouseEvent, ReactNode, useEffect, useRef } from 'react';
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import * as stylex from '@stylexjs/stylex';
import { Text } from '@figma/fpl-components';
import { borderRadius, color as token, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { LinearProgress } from '@/UI/LinearProgress/LinearProgress';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { color, EL_COLORS } from '@/colors';
import { LottieAnimation } from '@/components/Common/LottieAnimation';
import { MusicNoteIcon } from '@/UI/Icons/MusicNoteIcon';
import { PlayFilledIcon } from '@/UI/Icons/PlayFilledIcon';
import { FlexCenHor, FlexCenHorVer, FlexColCenHorVer } from '@/UI/styles';
import { assetUrl, assetThumbnailUrl, assetViewUrl } from '@/utils/assetUrls';
import { useResolvedAssets } from '@/state/workflow.state';
import { MediaLoadingOverlay } from '@/components/Common/FileUploader/Canvas/MediaLoadingOverlay';
import { EngineCanvasSurface } from '@/components/Recipe/FlowComponents/EngineCanvasSurface';
import { useIsThreeDModelLoading } from '@/state/threeDModelLoading.state';
import ThreeDeeViewer from '../../Nodes/LazyThreeDeeViewer';
import { TextViewer } from './TextViewer';
import type { ThreeDProps } from './types';
import type { MediaAsset, RenderingAsset, TextAsset } from '@/types/api/assets';

interface GalleryFileViewerProps extends Partial<ThreeDProps> {
  audioClassName?: string;
  container: 'node' | 'gallery' | 'design-app';
  presentation?: 'default' | 'preview';
  file: MediaAsset | TextAsset | RenderingAsset;
  handleImageLoad?: () => void;
  index: number;
  isHovered?: boolean;
  maxSteps?: number;
  onContextMenu?: (event: React.MouseEvent, index: number) => void;
  onTextChange?: (index: number, newValue: string) => void;
  overlay?: React.ReactNode;
  // eslint-disable-next-line react19-ref/require-ref-object-null
  resetTransformRef?: React.RefObject<ReactZoomPanPinchRef>;
  selected: number;
  setScale?: (scale: number) => void;
  transformKey?: number;
  // eslint-disable-next-line react19-ref/require-ref-object-null
  videoRef?: React.RefObject<HTMLVideoElement>;
  isMultiFilesViewMode?: boolean;
  fixedItemSize?: number;
  suspendMediaPlayback?: boolean;
  nodeId?: string;
}

const Container = ({
  children,
  isSelected,
  onContextMenu,
  isMultiFilesViewMode,
  minHeight,
}: {
  children: ReactNode;
  isSelected: boolean;
  onContextMenu: (event: MouseEvent) => void;
  isMultiFilesViewMode: boolean;
  minHeight?: number;
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: minHeight ? `${minHeight}px` : undefined,
        display: isMultiFilesViewMode || isSelected ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onContextMenu={onContextMenu}
    >
      {children}
    </Box>
  );
};

const GALLERY_SIZE = { w: 600, h: 600 };
const NODE_SIZE = { w: 422, h: 422 };

export const GalleryFileViewer: React.FC<GalleryFileViewerProps> = ({
  audioClassName,
  cameraPosition,
  container,
  presentation = 'default',
  file,
  handleImageLoad,
  index,
  is3DLocked,
  isHovered,
  maxSteps,
  onContextMenu,
  onTextChange,
  overlay = null,
  resetTransformRef,
  selected,
  setCameraPosition,
  setScale,
  transformKey,
  videoRef,
  isMultiFilesViewMode = false,
  fixedItemSize,
  suspendMediaPlayback = false,
  nodeId,
}) => {
  const resolvedAssets = useResolvedAssets();
  const isPreview = presentation === 'preview';
  const isGallery = container === 'gallery' || container === 'design-app';
  const internalVideoElementRef = useRef<HTMLVideoElement>(null);
  // this is bit hacky, ideally we would want to have the video managed only in here
  // but since its a legacy component, we'll keep it for to support the single file view mode
  const videoElementRef = isMultiFilesViewMode ? internalVideoElementRef : (videoRef ?? internalVideoElementRef);
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const isEngineModelLoading = useIsThreeDModelLoading(file.type === '3D' ? file.url : undefined);

  const handleMediaPlayPause = (element: HTMLMediaElement | null) => {
    if (!element) {
      return;
    }
    if (suspendMediaPlayback || !isHovered) {
      element.pause();
      return;
    }
    void element.play().catch(() => {
      // Hover preview can race with drag teardown; ignore play() rejections.
    });
  };

  useEffect(() => {
    if (audioElementRef.current && index !== selected) {
      audioElementRef.current.pause();
    }
  }, [index, selected]);

  useEffect(() => {
    if (suspendMediaPlayback) {
      videoElementRef.current?.pause();
      audioElementRef.current?.pause();
    }
  }, [suspendMediaPlayback, videoElementRef]);

  // Handle video play on hover only in multi-files view mode
  useEffect(() => {
    if (!isMultiFilesViewMode || file.type !== 'video' || !videoElementRef) {
      return;
    }

    handleMediaPlayPause(videoElementRef.current);
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [isHovered, isMultiFilesViewMode, file.type, suspendMediaPlayback]);

  // Handle audio play on hover only in multi-files view mode
  useEffect(() => {
    if (!isMultiFilesViewMode || file.type !== 'audio') {
      return;
    }

    handleMediaPlayPause(audioElementRef.current);
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [isHovered, isMultiFilesViewMode, file.type, suspendMediaPlayback]);

  const onContextMenuClick = (event: React.MouseEvent) => {
    onContextMenu?.(event, index);
  };

  const onZoom = (ref: ReactZoomPanPinchRef) => {
    setScale?.(ref.state.scale);
  };

  const isSelected = index === selected;
  const containerSize = fixedItemSize ? { w: fixedItemSize, h: fixedItemSize } : isGallery ? GALLERY_SIZE : NODE_SIZE;
  const canRenderWithEngine = container === 'gallery' && nodeId !== undefined;

  if (file.type === 'text' && isGallery) {
    if (isPreview) {
      return (
        <div {...stylex.props(previewStyles.textContainer)}>
          <div {...stylex.props(previewStyles.text)}>
            <Text.div variant="body-large">{'value' in file ? file.value : ''}</Text.div>
          </div>
        </div>
      );
    }
    return (
      <Container isSelected={isSelected} onContextMenu={onContextMenuClick} isMultiFilesViewMode={isMultiFilesViewMode}>
        <FlexCenHorVer>
          <Box
            sx={{
              width: '480px',
              maxHeight: '500px',
              overflow: 'auto',
              bgcolor: token.bgSecondary,
              borderRadius: 1,
              border: `1px solid ${EL_COLORS.BoxBorder}`,
              p: 4,
            }}
          >
            <TextStdRg color={token.text}>{'value' in file ? file.value : ''}</TextStdRg>
          </Box>
        </FlexCenHorVer>
      </Container>
    );
  }

  return (
    <Container
      isSelected={isSelected}
      onContextMenu={onContextMenuClick}
      isMultiFilesViewMode={isMultiFilesViewMode}
      minHeight={fixedItemSize}
    >
      {file.type === 'image' && (
        <>
          {container === 'node' ? (
            <img
              src={!isSelected ? assetThumbnailUrl(file, resolvedAssets) : assetViewUrl(file, resolvedAssets)}
              draggable="false"
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                position: 'relative',
                aspectRatio: file.width && file.height ? `${file.width}/${file.height} auto` : undefined,
              }}
            />
          ) : (
            <TransformWrapper
              centerZoomedOut
              limitToBounds
              minScale={1}
              pinch={{
                step: 100,
              }}
              onZoom={onZoom}
              ref={resetTransformRef}
              key={transformKey}
            >
              <TransformComponent
                wrapperClass={isPreview ? stylex.props(previewStyles.transformWrapper).className : undefined}
                contentClass={isPreview ? stylex.props(previewStyles.transformContent).className : undefined}
              >
                <img
                  className={['media-container-dark', isPreview && stylex.props(previewStyles.image).className]
                    .filter(Boolean)
                    .join(' ')}
                  onLoad={handleImageLoad}
                  src={!isSelected ? assetThumbnailUrl(file, resolvedAssets) : assetUrl(file, resolvedAssets)}
                  draggable="false"
                  style={
                    isPreview ? undefined : { maxWidth: '100%', maxHeight: '100%', display: 'block', margin: 'auto' }
                  }
                />
              </TransformComponent>
            </TransformWrapper>
          )}
        </>
      )}

      {file.type === 'video' ? (
        <>
          <video
            key={`video-${index}`}
            crossOrigin="anonymous"
            draggable="false"
            src={assetUrl(file, resolvedAssets)}
            controls={isMultiFilesViewMode ? false : true}
            loop
            muted={isMultiFilesViewMode}
            ref={videoElementRef}
            {...(isPreview ? stylex.props(previewStyles.video) : {})}
            style={
              isPreview
                ? undefined
                : {
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    position: 'relative',
                    aspectRatio: file.width && file.height ? `${file.width}/${file.height} auto` : undefined,
                  }
            }
          />
          {isMultiFilesViewMode && !isHovered && (
            <Box
              sx={{
                position: 'absolute',
              }}
            >
              <PlayFilledIcon />
            </Box>
          )}
        </>
      ) : null}

      {(isMultiFilesViewMode || isSelected) && file.type === '3D' && (
        <FlexCenHor
          className={container === 'node' ? 'nowheel nodrag nopan' : ''}
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            alignItems: 'center',
          }}
        >
          {canRenderWithEngine ? (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <EngineCanvasSurface nodeId={nodeId} className="media-container-dark nodrag nopan nowheel" />
              {isEngineModelLoading ? <MediaLoadingOverlay clickThrough /> : null}
            </Box>
          ) : (
            <ThreeDeeViewer
              key={file.url}
              objUrl={assetUrl(file, resolvedAssets) ?? file.url}
              mime={file.mime}
              containerSize={containerSize}
              lockOrbit={is3DLocked || false}
              cameraPosition={cameraPosition || { x: -3, y: 4, z: 5 }}
              setCameraPosition={setCameraPosition}
              allowInteraction={!isMultiFilesViewMode}
              displayBackground={isMultiFilesViewMode}
            />
          )}
        </FlexCenHor>
      )}

      {file.type === 'audio' && (
        <>
          <FlexCenHorVer sx={{ height: '100%', width: '100%' }}>
            <audio
              className={audioClassName}
              crossOrigin="anonymous"
              draggable="false"
              src={assetUrl(file, resolvedAssets)}
              controls={isMultiFilesViewMode ? false : true}
              loop={true}
              ref={audioElementRef}
              {...(isPreview ? stylex.props(previewStyles.audio) : {})}
            />
          </FlexCenHorVer>
          {isMultiFilesViewMode && (
            <Box
              sx={{
                position: 'absolute',
              }}
            >
              {isHovered ? (
                <LottieAnimation width={42} height={42} loop={true} src="/audio-wave.lottie" />
              ) : (
                <MusicNoteIcon />
              )}
            </Box>
          )}
        </>
      )}

      {file.type === 'text' && !isGallery && (
        <TextViewer
          file={file}
          index={index}
          onTextChange={onTextChange}
          hasMultipleFiles={maxSteps !== undefined && maxSteps > 1}
          isMultiFilesViewMode={isMultiFilesViewMode}
          fixedItemSize={fixedItemSize}
        />
      )}

      {file.type === 'rendering' && (
        <FlexCenHorVer
          sx={{
            height: '100%',
            width: '100%',
            borderRadius: 2,
            border: `1px solid ${color.White64_T}`,
            backgroundColor: color.Black100,
          }}
        >
          {file.progress !== undefined ? (
            <FlexColCenHorVer sx={{ width: '80%', gap: 0.5 }}>
              <TextSmRg
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  fontSize: '2.75rem',
                  lineHeight: '2.8rem',
                  fontWeight: '500',
                }}
              >
                {file.progress}%
              </TextSmRg>
              <LinearProgress sx={{ width: '100%' }} color="weavy_cta" variant="determinate" value={file.progress} />
            </FlexColCenHorVer>
          ) : (
            <CircularProgress color="weavy_cta" />
          )}
        </FlexCenHorVer>
      )}
      {overlay}
      {container === 'node' && !isMultiFilesViewMode ? (
        <Box
          data-testid="gradient-overlay-container"
          sx={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.1s ease-in-out',
          }}
        >
          <FlexCenHor
            data-testid="bottom-gradient-overlay"
            sx={{
              position: 'absolute',
              bottom: 0,
              width: '100%',
              height: '80px',
              background: 'linear-gradient(to top,#343337D3, #6B6B7800)',
              pointerEvents: 'none',
              filter: 'blur(30px)',
              transform: 'scale(1.8)  translateY(30px)',
            }}
          />
          <FlexCenHor
            data-testid="top-gradient-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              width: '100%',
              height: '80px',
              background: 'linear-gradient(to bottom,#343337D3, #6B6B7800)',
              pointerEvents: 'none',
              filter: 'blur(30px)',
              transform: 'scale(1.8)  translateY(-30px)',
            }}
          />
        </Box>
      ) : null}
    </Container>
  );
};

const previewStyles = stylex.create({
  transformWrapper: { width: '100%', height: '100%' },
  transformContent: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' },
  image: {
    display: 'block',
    margin: 'auto',
    borderRadius: borderRadius.medium,
    maxWidth: `calc(100% - 2 * ${spacer.spacer24px})`,
    maxHeight: `calc(100% - 2 * ${spacer.spacer24px})`,
  },
  video: {
    width: '100%',
    height: '100%',
    display: 'block',
    position: 'relative',
    borderRadius: borderRadius.medium,
    objectFit: 'contain',
  },
  audio: { maxWidth: '100%' },
  textContainer: {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    width: 'min(480px, 100%)',
    maxHeight: '100%',
    overflow: 'auto',
    backgroundColor: token.bgSecondary,
    borderRadius: borderRadius.medium,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: token.border,
    padding: spacer.spacer32px,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
});
